// src/context/CartContext.js
import React, { createContext, useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { t } = useTranslation();
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [loadingItems, setLoadingItems] = useState({});

  // Load cart from localStorage on initial load
  // Load cart from localStorage on initial load
  useEffect(() => {
    const loadCartFromStorage = () => {
      try {
        const savedCart = localStorage.getItem("userCart");
        if (savedCart) {
          const cartData = JSON.parse(savedCart);
          const rawItems = cartData.items || [];

          // Repair legacy/corrupted entries: if an item has a selected
          // capacity or bottle variant, its price is already final and
          // discount must be 0. Old buggy adds could have saved a stale
          // top-level discount alongside the raw base price — fix that here
          // so it self-heals instead of showing wrong prices forever.
          const repairedItems = rawItems.map(item => {
            const product = item.product;
            if (!product) return item;

            const hasVariant = !!product.selectedCapacity;
            if (hasVariant && product.discount) {
              return {
                ...item,
                product: {
                  ...product,
                  discount: 0,
                },
              };
            }
            return item;
          });

          setCart(repairedItems);
          setCartTotal(cartData.total || 0);
        }
      } catch (error) {
        console.error("Error loading cart from storage:", error);
        localStorage.setItem("userCart", JSON.stringify({ items: [], total: 0 }));
      }
    };

    loadCartFromStorage();
  }, []);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    const cartData = {
      items: cart,
      total: cartTotal,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem("userCart", JSON.stringify(cartData));
  }, [cart, cartTotal]);

  // Calculate total price
  // Calculate total price - FIXED: Respect capacity selection (no discount)
  const calculateTotal = (cartItems) => {
    const total = cartItems.reduce((acc, item) => {
      const product = item.product;
      if (!product) return acc;

      // If product has selectedCapacity, use price directly (NO DISCOUNT)
      // Otherwise apply discount if available
      let itemPrice;
      if (product.selectedCapacity) {
        // Capacity price is already the final price - use it directly
        itemPrice = product.price;
      } else if (product.discount > 0) {
        // Apply discount to regular product
        itemPrice = product.price * (1 - product.discount / 100);
      } else {
        itemPrice = product.price;
      }

      return acc + itemPrice * item.quantity;
    }, 0);

    setCartTotal(parseFloat(total.toFixed(2)));
    return total;
  };

  // Add to cart with color and capacity support
  // Add to cart with color and capacity support
  const addToCart = async (product, quantity = 1) => {
    console.log("=== CART CONTEXT: addToCart START ===");
    console.log("Received product:", product);
    console.log("Product selectedColor:", product.selectedColor);
    console.log("Product selectedCapacity:", product.selectedCapacity);
    console.log("Quantity:", quantity);

    setLoadingItems(prev => ({ ...prev, [product._id]: true }));

    try {
      await new Promise(resolve => setTimeout(resolve, 300));

      const numQuantity = parseInt(quantity);
      if (isNaN(numQuantity) || numQuantity < 1) {
        throw new Error(t("invalid_quantity"));
      }

      console.log("Current cart before update:", cart);


      // In CartContext.js - addToCart function
      const existingCartItemIndex = cart.findIndex((item) => {
        // First check if it's the same product
        if (item.product._id !== product._id) return false;

        // Get capacity IDs
        const itemCapacityId = item.product.selectedCapacity?._id;
        const newCapacityId = product.selectedCapacity?._id;

        // Get bottle sizes
        const itemBottleSize = item.product.selectedBottleVariant?.bottleSize;
        const newBottleSize = product.selectedBottleVariant?.bottleSize;

        // Get color IDs
        const itemColorId = item.product.selectedColor?.id;
        const newColorId = product.selectedColor?.id;

        // Check capacity match
        const capacityMatch = (!itemCapacityId && !newCapacityId) ||
          (itemCapacityId && newCapacityId && itemCapacityId === newCapacityId);

        // Check bottle size match
        const bottleMatch = (!itemBottleSize && !newBottleSize) ||
          (itemBottleSize && newBottleSize && itemBottleSize === newBottleSize);

        // Check color match
        const colorMatch = (!itemColorId && !newColorId) ||
          (itemColorId && newColorId && itemColorId === newColorId);

        // All must match for it to be the same item
        return capacityMatch && bottleMatch && colorMatch;
      });

      console.log("Existing cart item index:", existingCartItemIndex);

      let updatedCart;

      // ============================================================
      // PLACE THE productToStore OBJECT HERE - This is the fix!
      // ============================================================
      const productToStore = {
        _id: product._id,
        title: product.title,
        displayTitle: product.displayTitle || product.title,
        price: product.selectedCapacity ? product.selectedCapacity.price : product.price,
        discount: product.selectedCapacity ? 0 : (product.discount || 0),
        image: product.image || product.images?.[0] || product.productImage || "",
        images: product.images || [],
        description: product.description,
        Item: product.selectedCapacity ? product.selectedCapacity.stock : product.Item,
        selectedColor: product.selectedColor || null,
        selectedCapacity: product.selectedCapacity || null,
        // 🔑 ADD THIS LINE - Store the bottle variant info
        selectedBottleVariant: product.selectedBottleVariant || null,
        colors: product.colors || [],
        colorStocks: product.colorStocks || [],
        ProductColor: product.ProductColor || [],
        capacityVariants: product.capacityVariants || [],
      };

      if (existingCartItemIndex > -1) {
        console.log("Updating existing cart item");
        updatedCart = [...cart];
        updatedCart[existingCartItemIndex] = {
          ...updatedCart[existingCartItemIndex],
          quantity: updatedCart[existingCartItemIndex].quantity + numQuantity,
          product: {
            ...updatedCart[existingCartItemIndex].product,
            ...productToStore,
          },
        };
        console.log("Updated quantity to:", updatedCart[existingCartItemIndex].quantity);
      } else {
        console.log("Creating new cart item");
        const newItem = {
          product: productToStore,
          quantity: numQuantity,
        };
        console.log("New item created:", newItem);
        updatedCart = [...cart, newItem];
      }

      console.log("Updated cart:", updatedCart);
      setCart(updatedCart);
      calculateTotal(updatedCart);

      // Build success message
      let details = [];
      if (product.selectedCapacity?.name) {
        details.push(product.selectedCapacity.name);
      }
      if (product.selectedColor?.name) {
        details.push(product.selectedColor.name);
      }
      const detailText = details.length > 0 ? ` (${details.join(' - ')})` : '';

      Swal.fire({
        icon: "success",
        title: t("cart_updated"),
        text: `${product.title}${detailText} - ${t("added_items", { count: numQuantity })}`,
        showConfirmButton: false,
        timer: 1000,
      });

    } catch (error) {
      console.error("Cart error:", error);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: error.message || t("failed_update_cart"),
      });
    } finally {
      setLoadingItems(prev => ({ ...prev, [product._id]: false }));
      console.log("=== CART CONTEXT: addToCart END ===");
    }
  };

  // Update cart item quantity with capacity support
  const updateCartItemQuantity = async (productId, quantity, colorId = null, capacityId = null) => {
    try {
      const numQuantity = parseInt(quantity);
      if (isNaN(numQuantity) || numQuantity < 1) {
        throw new Error(t("invalid_quantity"));
      }

      const updatedCart = cart.map(item => {
        if (item.product._id !== productId) return item;

        // Check if it's the same color
        const itemColorId = item.product.selectedColor?.id;
        if (colorId !== null && itemColorId !== colorId) return item;
        if (colorId === null && itemColorId) return item;

        // Check if it's the same capacity
        const itemCapacityId = item.product.selectedCapacity?._id;
        if (capacityId !== null && itemCapacityId !== capacityId) return item;
        if (capacityId === null && itemCapacityId) return item;

        return { ...item, quantity: numQuantity };
      });

      setCart(updatedCart);
      calculateTotal(updatedCart);

    } catch (error) {
      console.error("Error updating cart item:", error);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_update_cart_item"),
      });
    }
  };

  // Remove from cart with capacity support
  const removeFromCart = async (productId, colorId = null, capacityId = null) => {
    try {
      const updatedCart = cart.filter(item => {
        if (item.product._id !== productId) return true;

        // Check color match
        if (colorId && item.product.selectedColor?.id !== colorId) return true;
        if (!colorId && item.product.selectedColor) return true;

        // Check capacity match
        if (capacityId && item.product.selectedCapacity?._id !== capacityId) return true;
        if (!capacityId && item.product.selectedCapacity) return true;

        return false;
      });

      setCart(updatedCart);
      calculateTotal(updatedCart);

      Swal.fire({
        icon: "success",
        title: t("item_removed"),
        text: t("item_removed_from_cart"),
        showConfirmButton: false,
        timer: 1000,
      });

    } catch (error) {
      console.error("Error removing from cart:", error);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_remove_cart_item"),
      });
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      setCart([]);
      setCartTotal(0);

      Swal.fire({
        icon: "success",
        title: t("cart_cleared"),
        text: t("cart_has_been_cleared"),
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (error) {
      console.error("Error clearing cart:", error);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_clear_cart"),
      });
    }
  };

  // Sync cart to server (optional)
  const syncCartToServer = async () => {
    try {
      console.log("Cart would be synced to server here");
      return true;
    } catch (error) {
      console.error("Error syncing cart to server:", error);
      throw error;
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartTotal,
        loadingItems,
        addToCart,
        updateCartItemQuantity,
        removeFromCart,
        clearCart,
        syncCartToServer
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Correct with 356 line code changes
